import React, { useState, useEffect } from 'react';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import { Search, Laptop, Calendar, CreditCard, RefreshCw, Info, Eye } from 'lucide-react';

// ─── Tipos ───────────────────────────────────────────────────────────────────
interface FaqItem {
  id: number;
  titulo_opcion: string;
  contenido_respuesta: string;
  precio_sugerido?: number;
  moneda?: string;
}

interface SmartAccordionProps {
  profesionalId: string;
  onBookAppointment?: () => void;
}

// ─── Utilidades ──────────────────────────────────────────────────────────────
function resolveIcon(titulo: string) {
  const t = titulo.toLowerCase();
  if (t.includes('servicio') || t.includes('especialidad') || t.includes('ofrece')) return Search;
  if (t.includes('modalidad') || t.includes('atencion') || t.includes('atención') || t.includes('virtual')) return Laptop;
  if (t.includes('sesion') || t.includes('sesión') || t.includes('cita') || t.includes('consulta') || t.includes('duracion')) return Calendar;
  if (t.includes('pago') || t.includes('precio') || t.includes('tarifa') || t.includes('costo') || t.includes('honorario')) return CreditCard;
  if (t.includes('cancelacion') || t.includes('cancelación') || t.includes('reprogramar') || t.includes('politica')) return RefreshCw;
  return Info;
}

function sanitizeContent(text: string): string {
  return text.replace(/(\+?\d[\d\s\-().]{7,14}\d)/g, '[contacto reservado]');
}

// ─── Componente: Caja de Detalle ─────────────────────────────────────────────
function DetailBox({ item }: { readonly item: FaqItem }) {
  const [revealed, setRevealed] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Reset state and trigger CSS animation on item change
    setRevealed(false);
    setMounted(false);
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, [item.id]);

  return (
    <div style={{
      marginTop: '16px', backgroundColor: '#F8FAFC', borderRadius: '16px', padding: '16px',
      border: '1px solid #E2E8F0',
      opacity: mounted ? 1 : 0, transform: mounted ? 'translateY(0)' : 'translateY(-10px)',
      transition: 'opacity 0.25s ease-out, transform 0.25s ease-out'
    }}>
      <p style={{ fontSize: '15px', color: '#334155', lineHeight: '24px', margin: 0, whiteSpace: 'pre-wrap' }}>
        {sanitizeContent(item.contenido_respuesta)}
      </p>

      {item.precio_sugerido != null && (
        <div style={{ marginTop: '16px' }}>
          {revealed ? (
            <div style={{
              display: 'inline-flex', alignItems: 'baseline', gap: '8px', padding: '12px 20px',
              backgroundColor: '#0F172A', borderRadius: '100px', boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
              animation: 'fadeInUp 0.4s ease-out forwards'
            }}>
              <span style={{ fontSize: '13px', color: '#94A3B8', fontWeight: 500 }}>Tarifa base</span>
              <span style={{ fontSize: '20px', fontWeight: 900, color: '#FFFFFF', letterSpacing: '-0.5px' }}>
                {item.moneda === 'EUR' ? '€' : '$'}
                {Number(item.precio_sugerido).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          ) : (
            <button
              onClick={() => setRevealed(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                backgroundColor: '#EFF6FF', borderRadius: '100px', border: '1px solid #BFDBFE',
                cursor: 'pointer', transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#DBEAFE'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#EFF6FF'}
            >
              <Eye size={14} color="#0052CC" />
              <span style={{ fontSize: '13px', color: '#0052CC', fontWeight: 600 }}>Revelar tarifa de referencia</span>
            </button>
          )}
        </div>
      )}
      
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ─── Componente Principal ────────────────────────────────────────────────────
export function SmartAccordion({ profesionalId }: Readonly<SmartAccordionProps>) {
  const [items, setItems] = useState<FaqItem[]>([]);
  const [openId, setOpenId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from('profesional_servicios_faq')
      .select('id, titulo_opcion, contenido_respuesta, precio_sugerido, moneda')
      .eq('profesional_uuid', profesionalId)
      .order('id', { ascending: true })
      .then(({ data, error }) => {
        if (!error && data) setItems(data as FaqItem[]);
        setLoading(false);
      });
  }, [profesionalId]);

  if (loading) {
    return (
      <div style={{ padding: '24px 0', display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '24px', height: '24px', border: '2px solid #94A3B8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (items.length === 0) return null;

  const toggle = (id: number) => {
    setOpenId(prev => (prev === id ? null : id));
  };

  const selectedItem = items.find(i => i.id === openId);

  return (
    <div style={{ marginBottom: '8px' }}>
      <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A', marginBottom: '12px', marginTop: 0 }}>
        Información del profesional
      </h3>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {items.map(item => {
          const isActive = openId === item.id;
          const Icon = resolveIcon(item.titulo_opcion);
          return (
            <button
              key={item.id}
              onClick={() => toggle(item.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px',
                borderRadius: '100px', border: `1px solid ${isActive ? '#0F172A' : '#E2E8F0'}`,
                backgroundColor: isActive ? '#0F172A' : '#F8FAFC',
                cursor: 'pointer', transition: 'all 0.2s', outline: 'none'
              }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = '#F1F5F9'; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = '#F8FAFC'; }}
            >
              <Icon size={14} color={isActive ? '#FFFFFF' : '#64748B'} />
              <span style={{ fontSize: '14px', fontWeight: isActive ? 600 : 500, color: isActive ? '#FFFFFF' : '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.titulo_opcion}
              </span>
            </button>
          );
        })}
      </div>

      {selectedItem && (
        <DetailBox item={selectedItem} />
      )}
    </div>
  );
}
